import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

export const ConfigurationContext = createContext({
  departments: [],
  loading: false,
  error: null,
  fetchDepartments: () => {},
  updateDepartment: () => {},
  addDepartment: () => {},
  deleteDepartment: () => {},
});

export function ConfigurationProvider({ children }) {
  const [departments, setDepartments] = useState(() => {
    // Try to load from localStorage first
    const cached = localStorage.getItem('departments-config');
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        console.warn('Failed to parse cached departments config');
      }
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);
  const { user } = useAuth0();

  // Fetch departments configuration from local config file with localStorage cache
  const fetchDepartments = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    // Check if we have recent cached data and don't need to refresh
    if (!forceRefresh && lastFetch && (now - lastFetch) < CACHE_DURATION) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Try local config first
      const response = await fetch('/config/departments.json', {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      let departmentsData = [];
      if (response.ok) {
        const data = await response.json();
        departmentsData = Array.isArray(data?.departments) ? data.departments : [];
      } else {
        // If config file missing, fall back to baked-in defaults
        departmentsData = [
          { id: 1, name: 'Pastoral Care', email: 'jsmith@crossroads.ca', active: true },
          { id: 2, name: 'Prayer Ministry', email: 'gwilloughby@crossroads.ca', active: true }
        ];
      }
      setDepartments(departmentsData);
      
      // Cache the data
      localStorage.setItem('departments-config', JSON.stringify(departmentsData));
      setLastFetch(now);
    } catch (err) {
      setError('Failed to fetch departments configuration');
      console.error(err);
      // Use fallback configuration on error
      const fallbackDepartments = [
        { id: 1, name: 'Pastoral Care', email: 'support@a1p-prayercenter.bolddesk.com', active: true },
        { id: 2, name: 'Prayer Ministry', email: 'gwilloughby@crossroads.ca', active: true }
      ];
      setDepartments(fallbackDepartments);
      localStorage.setItem('departments-config', JSON.stringify(fallbackDepartments));
      setLastFetch(now);
    } finally {
      setLoading(false);
    }
  }, [lastFetch]);

  // Update department configuration
  const updateDepartment = useCallback(async (id, updates) => {
    try {
      setError(null);

      const updatedDepartments = departments.map(dept => (
        dept.id === id ? { ...dept, ...updates } : dept
      ));
      setDepartments(updatedDepartments);
      localStorage.setItem('departments-config', JSON.stringify(updatedDepartments));

      toast.success('Department updated successfully');
      return updates;
    } catch (err) {
      setError('Failed to update department');
      toast.error(err.message || 'Failed to update department');
      console.error(err);
      throw err;
    }
  }, [departments]);

  // Add new department
  const addDepartment = useCallback(async (departmentData) => {
    try {
      setError(null);

      const nextId = departments.length
        ? Math.max(...departments.map(d => Number(d.id) || 0)) + 1
        : 1;
      const newDept = { id: nextId, ...departmentData };

      const updatedDepartments = [...departments, newDept];
      setDepartments(updatedDepartments);
      localStorage.setItem('departments-config', JSON.stringify(updatedDepartments));

      toast.success('Department added successfully');
      return newDept;
    } catch (err) {
      setError('Failed to add department');
      toast.error(err.message || 'Failed to add department');
      console.error(err);
      throw err;
    }
  }, [departments]);

  // Delete department
  const deleteDepartment = useCallback(async (id) => {
    try {
      setError(null);

      const updatedDepartments = departments.filter(dept => dept.id !== id);
      setDepartments(updatedDepartments);
      localStorage.setItem('departments-config', JSON.stringify(updatedDepartments));

      toast.success('Department deleted successfully');
    } catch (err) {
      setError('Failed to delete department');
      toast.error(err.message || 'Failed to delete department');
      console.error(err);
      throw err;
    }
  }, [departments]);

  // Auto-fetch departments on mount
  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  // Set up periodic refresh every 5 minutes for configuration updates
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDepartments(true); // Force refresh
    }, 300000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [fetchDepartments]);

  return (
    <ConfigurationContext.Provider value={{
      departments,
      loading,
      error,
      fetchDepartments,
      updateDepartment,
      addDepartment,
      deleteDepartment,
    }}>
      {children}
    </ConfigurationContext.Provider>
  );
}

export const useConfiguration = () => useContext(ConfigurationContext);
